import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Calendar from "react-calendar";
import dayjs from "dayjs";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

import {
  FilterPopover,
  FilterTrigger,
  filterStyles,
} from "Components/UI/TableFilters/TableFilters";
import { format } from "Shared/Utils/shared";
import { API_DATE_FORMAT } from "Shared/Constants/app";
import { LABEL_FORMATS } from "Shared/Utils/display";

type ValuePiece = Date | null;

export type Value = ValuePiece | [ValuePiece, ValuePiece];
type DateRangeProps = {
  onDateChange?: (searchParams: URLSearchParams) => void;
  rangeStartName?: string;
  rangeEndName?: string;
  dateRangeName?: string;
};

const toDate = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

/** "Sep 1 – Sep 30, 2026"; the year shows once when both ends share it. */
export const formatDateRangeLabel = (from: Date, to: Date) => {
  const start = dayjs(from);
  const end = dayjs(to);
  const sameYear = start.year() === end.year();
  return `${start.format(sameYear ? LABEL_FORMATS.monthDay : LABEL_FORMATS.monthDayYear)} – ${end.format(LABEL_FORMATS.monthDayYear)}`;
};

/** Date range chip; picks a start and end day in a calendar and writes them to the URL. */
const DateRange: React.FC<DateRangeProps> = ({
  onDateChange,
  rangeStartName = "from",
  rangeEndName = "to",
  dateRangeName = "range_date",
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const from = toDate(searchParams.get(rangeStartName));
  const to = toDate(searchParams.get(rangeEndName));

  const changeDate = (value: Value) => {
    if (value && Array.isArray(value)) {
      if (value[0]) {
        searchParams.set(
          rangeStartName,
          format(value[0], API_DATE_FORMAT) ?? "",
        );
      }
      if (value[1]) {
        searchParams.set(
          rangeEndName,
          format(value[1], API_DATE_FORMAT) ?? "",
        );
      }
      searchParams.set(dateRangeName, "true");
    } else {
      searchParams.delete(rangeStartName);
      searchParams.delete(rangeEndName);
      searchParams.delete(dateRangeName);
    }
    searchParams.delete("range");
    searchParams.delete("page");
    onDateChange?.(searchParams);
    setSearchParams(searchParams);
  };

  return (
    <>
      <FilterTrigger
        label="Dates"
        value={from && to ? formatDateRangeLabel(from, to) : undefined}
        active={!!from}
        open={!!anchor}
        icon={<CalendarTodayOutlinedIcon aria-hidden className={filterStyles.triggerIcon} />}
        onClick={(event) => setAnchor(event.currentTarget)}
        onClear={() => changeDate(null)}
        clearLabel="Clear dates"
      />
      <FilterPopover
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        label="Choose dates"
        className={filterStyles.calendarPanel}
      >
        <div className={filterStyles.calendar}>
          <Calendar
            selectRange
            value={from && to ? [from, to] : null}
            defaultActiveStartDate={from ?? undefined}
            onChange={(value) => {
              changeDate(value as Value);
              setAnchor(null);
            }}
          />
        </div>
      </FilterPopover>
    </>
  );
};
export default DateRange;
